<template>
  <div id="payment-adjustments" class="align-start fill-height">
    <v-row class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0" :ref="pageRef">{{ $route.meta.title }}</h2>

        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="4" md="3" lg="2">
            <i-instructions
              instructionKey="reports/payment-adjustments"
            ></i-instructions>
          </v-col>
        </v-row>

        <i-search-sort
          ref="searchSort"
          dateRangeColumnName="Created Date"
          :loading="fetching"
          :routeName="$route.name"
          @searchQuery="searchQuery"
        />

        <i-progress-linear v-if="fetching && $vuetifyDisplay.smAndDown" />

        <template v-if="!fetching && mappedAdjustments.length === 0">
          <i-empty-results
            buttonText="Back to Reports"
            buttonRouteName="reports"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <template v-if="$vuetifyDisplay.smAndDown">
            <v-row no-gutters>
              <v-col
                cols="12"
                v-for="item in mappedAdjustments"
                :key="item.referenceNumber"
                class="mt-0 pa-0"
              >
                <v-card class="rounded-2 pa-4 pa-md-6 mb-6">
                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span
                        ><strong>{{ headers[0].text }}</strong></span
                      >
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span
                        ><strong>{{ item.referenceNumber }}</strong></span
                      >
                    </v-col>
                  </v-row>

                  <v-divider class="my-2"></v-divider>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[1].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{ item.linkedReferenceNumber }}</span>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[2].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(item.createdAt)
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[3].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span v-if="item.store">{{ item.store.name }}</span>
                      <span v-else>{{ item.storeName }}</span>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[4].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{ item.creditTo }}</span>
                    </v-col>
                  </v-row>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[5].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.currency(displayAdjustmentTotal(item))
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-btn
                    rounded
                    class="i-btn-block-secondary g-full-width mt-4"
                    :to="{
                      name: 'payment-adjustment-details',
                      params: { paymentAdjustmentId: item.id }
                    }"
                  >
                    <strong>View details</strong>
                  </v-btn>

                  <div
                    class="text-center pt-2"
                    v-if="item.paymentTransactionExpectedPeriodEndDate"
                  >
                    <v-btn
                      class="g-full-width i-btn-block-primary mt-1"
                      :to="paymentTransactionsRouteTo(item)"
                    >
                      <span>Payment Transaction Details</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-list-box-outline</v-icon
                      >
                    </v-btn>
                  </div>

                  <div class="text-center pt-2">
                    <v-btn
                      class="g-full-width i-btn-block-primary mt-1"
                      @click="fetchPDF(item.id)"
                    >
                      <span>Export PDF</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-file-pdf-box</v-icon
                      >
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <template v-else>
            <v-data-table
              class="pt-2 elevation-1 i-data-table"
              :headers="headers"
              hide-default-footer
              :items="mappedAdjustments"
              :items-per-page="pageSize"
              :loading="fetching"
              :no-data-text="emptyFilteredResultsText"
              :no-results-text="emptyFilteredResultsText"
            >
              <template v-slot:[`header.totalIncTax`]="{ header }">
                {{ header.text }}
              </template>

              <template v-slot:[`header.referenceNumber`]="{ header }">
                <span
                  class="i-sortable-header"
                  @click="handleHeaderSort('referenceNumber')"
                >
                  {{ header.text }}
                  <v-icon v-if="isSortedBy('referenceNumber')" small>
                    {{
                      isSortAsc('referenceNumber')
                        ? 'mdi-arrow-up'
                        : 'mdi-arrow-down'
                    }}
                  </v-icon>
                </span>
              </template>

              <template v-slot:[`header.store.name`]="{ header }">
                <span
                  class="i-sortable-header"
                  @click="handleHeaderSort('store.name')"
                >
                  {{ header.text }}
                  <v-icon v-if="isSortedBy('store.name')" small>
                    {{
                      isSortAsc('store.name')
                        ? 'mdi-arrow-up'
                        : 'mdi-arrow-down'
                    }}
                  </v-icon>
                </span>
              </template>

              <template v-slot:[`item.referenceNumber`]="{ item }">
                <span>{{ item.referenceNumber }}</span>
              </template>

              <template v-slot:[`item.creditTo`]="{ item }">
                <span>{{ item.creditTo }}</span>
              </template>

              <template v-slot:[`item.linkedReferenceNumber`]="{ item }">
                <span>{{ item.linkedReferenceNumber }}</span>
              </template>

              <template v-slot:[`item.createdAt`]="{ item }">
                <span>{{ $filters.formatDateTime12h(item.createdAt) }}</span>
              </template>

              <template v-slot:[`item.totalIncTax`]="{ item }">
                <span>{{
                  $filters.currency(displayAdjustmentTotal(item))
                }}</span>
              </template>

              <template v-slot:[`item.store.name`]="{ item }">
                <span v-if="item.store">{{ item.store.name }}</span>
                <span v-else>{{ item.storeName }}</span>
              </template>

              <template v-slot:[`item.details`]="{ item }">
                <v-btn
                  rounded
                  class="i-btn-block-secondary p-btn"
                  :to="{
                    name: 'payment-adjustment-details',
                    params: { paymentAdjustmentId: item.id }
                  }"
                >
                  <strong>View</strong>
                </v-btn>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  :disabled="!item.paymentTransactionExpectedPeriodEndDate"
                  icon
                  :to="paymentTransactionsRouteTo(item)"
                  v-tooltip="{
                    content: 'View related Payment Transaction details',
                    visible: $vuetifyDisplay.mdAndDown ? false : undefined
                  }"
                >
                  <v-icon class="i-btn-payment-transactions"
                    >mdi-list-box-outline</v-icon
                  >
                </v-btn>

                <v-btn
                  icon
                  v-tooltip="{
                    content: 'Export PDF',
                    visible: $vuetifyDisplay.mdAndDown ? false : undefined
                  }"
                  @click="fetchPDF(item.id)"
                >
                  <v-icon class="i-btn-pdf">mdi-file-pdf-box</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </template>

          <i-paginator
            :currentPage="currentPage"
            :pageRef="'pageTitle'"
            :pageSize="pageSize"
            :pagination="pagination"
            @paginate="handlePagination"
          />
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  fetchPaymentAdjustmentHistory,
  fetchPaymentAdjustmentPdfUrl
} from '@/services/paymentAdjustment'
import iPaginator from '@/components/Paginator'
import iProgressLinear from '@/components/ProgressLinear'
import iSearchSort from '@/components/SearchSort'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import { openFile, routeToPaymentTransactions } from '@/utils/common'
import { pickSupplierAmount } from '@/utils/supplierAmountDisplay'

export default {
  components: {
    iPaginator,
    iProgressLinear,
    iSearchSort,
    iInstructions,
    iEmptyResults
  },

  data: () => ({
    currentPage: 1,
    fetching: true,
    adjustments: [],
    pageRef: 'pageTitle',
    pageSize: 10,
    pagination: {
      total: 0,
      totalPages: 0
    },
    query: null
  }),

  computed: {
    ...mapGetters('tenant', [
      'isILR',
      'tenantCountryRegion',
      'usesAustralianTaxLabels'
    ]),
    headers() {
      return [
        {
          text: 'PAYMENT ADJUSTMENT REFERENCE NUMBER',
          value: 'referenceNumber',
          sortable: false
        },
        {
          text: 'RELATED TO',
          value: 'linkedReferenceNumber',
          sortable: false
        },
        {
          text: 'CREATED DATE',
          value: 'createdAt',
          sortable: false
        },
        {
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          value: 'store.name',
          sortable: false
        },
        {
          text: 'CREDIT TO',
          value: 'creditTo',
          sortable: false
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'TOTAL AMOUNT (INC TAX)'
            : 'TOTAL AMOUNT',
          value: 'totalIncTax',
          sortable: false
        },
        {
          text: 'DETAILS',
          value: 'details',
          align: 'center',
          sortable: false
        },
        {
          text: 'ACTIONS',
          value: 'actions',
          align: 'center',
          sortable: false
        }
      ]
    },

    emptyFilteredResultsText() {
      return `No Payment Adjustments found`
    },

    mappedAdjustments() {
      return this.adjustments.map((order) => {
        return { ...order }
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    handleHeaderSort(value) {
      this.$refs.searchSort?.sortElements(value)
    },

    isSortedBy(value) {
      return this.$refs.searchSort?.sortBy === value
    },

    isSortAsc(value) {
      return !!this.$refs.searchSort?.sortByOptions.find(
        (option) => option.value === value
      )?.asc
    },

    displayAdjustmentTotal(item) {
      return pickSupplierAmount({
        tenantCountryRegion: this.tenantCountryRegion,
        exTax: item.totalExTax,
        incTax: item.totalIncTax
      })
    },

    fetch(currentPage, pageSize, query) {
      this.fetching = true
      currentPage -= 1 // currentPage decreased by 1 as currentPage API starts from 0

      fetchPaymentAdjustmentHistory(currentPage, pageSize, query)
        .then((response) => {
          this.adjustments = response.paymentAdjustments
          this.pagination = response.pagination
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetching = false
        })
    },

    fetchPDF(adjustmentId) {
      fetchPaymentAdjustmentPdfUrl(adjustmentId)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Adjustment ${adjustmentId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
    },

    handlePagination(page) {
      this.currentPage = page
      this.fetching = true
      this.adjustments = []
      this.fetch(this.currentPage, this.pageSize, this.query)
      if (this.$refs[this.pageRef])
        this.$refs[this.pageRef].scrollIntoView({ behavior: 'smooth' })
    },

    searchQuery(queryObject) {
      if (queryObject.restartPagination) this.currentPage = 1

      this.query = queryObject.query
      this.fetch(this.currentPage, this.pageSize, this.query)
    },

    paymentTransactionsRouteTo(paymentAdjustment) {
      return routeToPaymentTransactions(
        paymentAdjustment,
        paymentAdjustment.referenceNumber
      )
    }
  },

  mounted() {
    this.fetch(this.currentPage, this.pageSize)
  }
}
</script>

<style lang="scss" scoped>
#payment-adjustments {
  .p-btn-pdf {
    color: $link-color;

    &:before {
      font-size: 32px;
    }
  }
}

@media only screen and (min-width: 1264px) {
  #payment-adjustments {
    .p-btn {
      width: 125px;
    }
  }
}
</style>
